import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { router } from '@inertiajs/react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { Button, Card, Empty, message } from 'antd';
import ProjectRow from '../../components/tracker/Projects/ProjectRow';
import ProjectFormModal from '../../components/tracker/Projects/ProjectFormModal';
import api from '../../lib/api';
import type { Project, ProjectsPageProps } from '../../types/project';

const Projects: React.FC<ProjectsPageProps> = ({ projects }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Project | null>(null);

  // 活跃项目的本地顺序：拖拽过程中只在这里乐观重排（没有网络请求，很便宜）；
  // props.projects 变化时（归档/删除/新建之后 router.reload 回来的新数据）
  // 重新同步。
  const [activeOrder, setActiveOrder] = useState<Project[]>(() =>
    projects.filter((project) => !project.archived)
  );

  // 拖拽结束时要拿到"当下最新"的顺序去落库，但 useDrag 的 end 回调闭包创建时
  // 可能早于后续的多次 hover 重排——用 ref 而不是直接读 activeOrder，避免
  // 拿到创建那一刻的旧值。
  const activeOrderRef = useRef(activeOrder);
  activeOrderRef.current = activeOrder;

  useEffect(() => {
    setActiveOrder(projects.filter((project) => !project.archived));
  }, [projects]);

  const archived = useMemo(() => projects.filter((project) => project.archived), [projects]);

  const refresh = () => router.reload({ only: ['projects'] });

  useEffect(() => {
    // 改动此时已经在服务端提交了。局部刷新（router.reload）如果因为真正的网络层
    // 失败（离线/超时/DNS，Request.send() 第二个 .catch 分支）而没跑完，
    // 界面会停在旧数据上却毫无提示——用户可能对着已归档的项目再点一次「归档」，
    // 然后收到一个看不懂的错误。这种失败不会触发 router.reload 的 onError
    // （onError 只在响应带 X-Inertia 头且 page.props.errors 非空时触发，见
    // Response.process()），而是触发全局的 exception 事件，所以在这里订阅它。
    const stop = router.on('exception', () => {
      message.error('列表刷新失败，请手动刷新页面');
    });
    return stop;
  }, []);

  const openCreate = () => {
    setEditing(null);
    setModalOpen(true);
  };

  const openEdit = (project: Project) => {
    setEditing(project);
    setModalOpen(true);
  };

  const handleSaved = () => {
    setModalOpen(false);
    refresh();
  };

  const withErrorToast = async (action: () => Promise<unknown>) => {
    try {
      await action();
      refresh();
    } catch (error) {
      message.error(error instanceof Error ? error.message : '操作失败');
    }
  };

  const toggleArchive = (project: Project) =>
    withErrorToast(() =>
      project.archived ? api.projects.unarchive(project.id) : api.projects.archive(project.id)
    );

  const remove = (project: Project) => withErrorToast(() => api.projects.destroy(project.id));

  const moveProject = useCallback((dragIndex: number, hoverIndex: number) => {
    setActiveOrder((prev) => {
      const next = [...prev];
      const [dragged] = next.splice(dragIndex, 1);
      next.splice(hoverIndex, 0, dragged);
      return next;
    });
  }, []);

  // 只在拖拽结束时调一次，不要挪到 moveProject 里——moveProject 在一次拖拽里
  // 会触发很多次。
  const persistOrder = useCallback(async () => {
    const ids = activeOrderRef.current.map((project) => project.id);
    try {
      await api.projects.reorder(ids);
      refresh();
    } catch (error) {
      message.error(error instanceof Error ? error.message : '排序保存失败');
      refresh(); // 失败也刷新一次，回到服务端的真实顺序，不留在过期的乐观状态上
    }
  }, []);

  return (
    <>
      <DndProvider backend={HTML5Backend}>
        <div className="mx-auto max-w-3xl px-4 py-6">
          <div className="mb-5 flex items-end justify-between">
            <div>
              <h1 className="text-xl font-semibold text-gray-900">项目</h1>
              <p className="mt-1 text-sm text-gray-500">
                项目是活儿的归属；生活面向标签独立并存；拖动可调整顺序
              </p>
            </div>
            <Button type="primary" onClick={openCreate}>
              新建项目
            </Button>
          </div>

          <Card size="small" styles={{ body: { padding: 0 } }}>
            {activeOrder.length === 0 ? (
              <Empty description="还没有项目" className="py-8" />
            ) : (
              activeOrder.map((project, index) => (
                <ProjectRow
                  key={project.id}
                  project={project}
                  index={index}
                  draggable
                  moveProject={moveProject}
                  onDropped={persistOrder}
                  onEdit={openEdit}
                  onToggleArchive={toggleArchive}
                  onDelete={remove}
                />
              ))
            )}
          </Card>

          {archived.length > 0 && (
            <>
              <h2 className="mb-2 mt-6 text-sm font-medium text-gray-500">已归档</h2>
              <Card size="small" styles={{ body: { padding: 0 } }}>
                {archived.map((project, index) => (
                  <ProjectRow
                    key={project.id}
                    project={project}
                    index={index}
                    onEdit={openEdit}
                    onToggleArchive={toggleArchive}
                    onDelete={remove}
                  />
                ))}
              </Card>
            </>
          )}

          <ProjectFormModal
            open={modalOpen}
            project={editing}
            onCancel={() => setModalOpen(false)}
            onSaved={handleSaved}
          />
        </div>
      </DndProvider>
    </>
  );
};

export default Projects;
